import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, Paper, TablePagination, Typography } from '@mui/material';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import withStyles from '@mui/styles/withStyles';

import { Loader } from 'components/loading-overlay/loading-overlay';
import { notifyError } from 'actions/action-notifications';
import {
  fetchFinanceExtract,
  downloadFinanceExtract,
} from 'services/utils/finance-extract-service';
import { styles } from '../report-styles';

const EMPTY_ARRAY = [];
const COLUMN_MIN_WIDTH_PX = 200;

const SPECIAL_HEADERS_LABELS = {
  'ar_paid': 'AR Paid',
  'cps_revenue': 'CPS Revenue',
  'patient_dob': 'Patient DOB',
  'pharmacy_npi': 'Pharmacy NPI',
  'sp_cdm': 'SP CDM',
  'fc_dates': 'FC Dates',
  'fdc_dates': 'FDC Dates',
  'ndc': 'NDC',
  'provider_npi': 'Provider NPI',
  'is_gtd': 'Is GTD',
  'cfs_rate': 'CFS Rate',
  'updated_ts': 'Updated TS',
  'primary_payor_pcn': 'Primary Payor PCN',
  'primary_payor_bin': 'Primary Payor BIN',
  'secondary_payor_bin': 'Secondary Payor BIN',
  'secondary_payor_pcn': 'Secondary Payor PCN',
  'tertiary_payor_bin': 'Tertiary Payor BIN',
  'tertiary_payor_pcn': 'Tertiary Payor PCN',
  '340b_qualifier': '340B Qualifier',
  'p1_evoucher': 'P1 EVoucher',
  'calculated_accumulation_340b': 'Calculated Accumulation 340B',
};

const DATE_COLUMNS = ['for_month', 'patient_dob'];
const DATE_TIME_COLUMNS = ['rx_picked_up', 'updated_ts', 'fill_date'];

const isIdentifierColumn = key => {
  return typeof key === 'string' && key.toLowerCase() === 'id';
};

const getColumnLabel = key => {
  if (!key) {
    return '';
  }

  return startCase(key.replace(/_/g, ' '));
};

const normalizeColumns = rows => {
  if (Array.isArray(rows) && rows.length && typeof rows[0] === 'object' && rows[0] !== null) {
    return Object.keys(rows[0]).reduce((accumulator, key) => {
      if (isIdentifierColumn(key)) {
        return accumulator;
      }

      accumulator.push({
        key,
        label: SPECIAL_HEADERS_LABELS[key] || getColumnLabel(key),
      });

      return accumulator;
    }, []);
  }

  return EMPTY_ARRAY;
};

const formatCellValue = (value, columnKey) => {
  if (!value) {
    return '';
  }

  if (DATE_COLUMNS.includes(columnKey)) {
    return moment(value).format('MM/DD/YYYY');
  }

  if (DATE_TIME_COLUMNS.includes(columnKey)) {
    return moment(value).format('MM/DD/YYYY HH:mm:ss');
  }

  return value;
};

const isNumericColumn = (rows, columnKey) => {
  if (!rows.length) {
    return false;
  }

  // Check first non-null value to determine if column is numeric
  const firstNonNullRow = rows.find(row => {
    const value = row?.[columnKey];
    return value !== null && value !== undefined && value !== '';
  });

  if (firstNonNullRow) {
    const value = firstNonNullRow[columnKey];
    return typeof value === 'number' || !Number.isNaN(parseFloat(value));
  }

  return false;
};

const FinanceExtractTab = ({ classes }) => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState(EMPTY_ARRAY);
  const [columns, setColumns] = useState(EMPTY_ARRAY);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const cursors = React.useRef({ 0: 0 });

  const [totalCount, setTotalCount] = useState(0);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const lastId = cursors.current[page] ?? 0;
      const response = await fetchFinanceExtract({ lastId, size: rowsPerPage });
      const data = response?.data?.data ?? EMPTY_ARRAY;
      const count = response?.data?.totalCount ?? 0;
      const normalizedColumns = normalizeColumns(data);

      setRows(data);
      setTotalCount(count);
      setColumns(normalizedColumns);

      if (data.length > 0) {
        const lastRow = data[data.length - 1];
        const lastRowId = lastRow?.id ?? lastRow?.Id ?? lastRow?.ID;
        if (lastRowId) {
          cursors.current[page + 1] = lastRowId;
        }
      }
    } catch (error) {
      dispatch(notifyError('Unable to load finance extract report.'));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, page, rowsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownload = async () => {
    setIsLoading(true);
    try {
      const response = await downloadFinanceExtract();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finance-extract-${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      dispatch(notifyError('Unable to download finance extract report.'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderDownloadButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleDownload}
        disabled={!rows.length || !columns.length}
      >
        Download CSV
      </Button>
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    cursors.current = { 0: 0 };
  };

  const renderTableBody = () => {
    if (!rows.length) {
      return (
        <tr>
          <td colSpan={columns.length || 1} className={classes.generalReportTbodyTr}>
            <Typography align="center" variant="body2">
              No data available.
            </Typography>
          </td>
        </tr>
      );
    }

    return rows.map((row, rowIndex) => {
      const rowKey = row?.id ?? row?.Id ?? row?.ID ?? `finance-extract-row-${rowIndex}`;

      return (
        <tr key={rowKey} className={rowIndex % 2 !== 0 ? classes.oddRow : undefined}>
          {columns.map(column => {
            const isNumeric = isNumericColumn(rows, column.key);
            return (
              <td
                key={`${column.key}-${rowKey}`}
                className={classes.generalReportTbodyTr}
                style={{ textAlign: isNumeric ? 'right' : 'left', paddingLeft: 8, paddingRight: 8 }}
              >
                {formatCellValue(row?.[column.key], column.key)}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <Loader loaded={!isLoading}>
      <Box>
        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
          <Grid item>
            <Typography variant="h6">Finance Extract</Typography>
          </Grid>
          <Grid item>{renderDownloadButton()}</Grid>
        </Grid>

        <Paper>
          <div className={classes.generalReportTable}>
            <div
              className={classes.generalReportTableContainer}
              style={{ maxHeight: 'calc(100vh - 350px)' }}
            >
              <table className={classes.generalReportTableMain}>
                <thead>
                  <tr>
                    {columns.length ? (
                      columns.map(column => (
                        <th
                          key={column.key}
                          className={classes.generalReportHeaderTh}
                          style={{ paddingLeft: 8, paddingRight: 8 }}
                        >
                          <div className={classes.generalReportHeaderTr}>
                            <strong>{column.label}</strong>
                          </div>
                        </th>
                      ))
                    ) : (
                      <th className={classes.generalReportHeaderTh}>
                        <div className={classes.generalReportHeaderTr}>
                          <Typography variant="body2">No columns available</Typography>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className={classes.generalReportMainTbody}>{renderTableBody()}</tbody>
              </table>
            </div>
          </div>
          <TablePagination
            rowsPerPageOptions={[50, 100, 200, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </Loader>
  );
};

export default withStyles(styles, { withTheme: true })(FinanceExtractTab);
